// utils/iepEngine.js caused failed to compile
class IEPEngine {
  constructor(iepData) {
    this.data = iepData;
    this.transformations = iepData.transformations;
    this.dimensions = iepData.dimensions;
  }

  // Calculate organization maturity score - FIXED VERSION
  calculateMaturityScore(organizationData) {
    // ✅ FIX: Check if organizationData.dimensions exists, use default if not
    const orgDimensions = organizationData.dimensions || {};
    
    const dimensionScores = Object.keys(this.dimensions).map(dimId => {
      const dimension = this.dimensions[dimId];
      const orgScore = orgDimensions[dimId] || 50; // ✅ Default to 50 if no score
      const normalizedScore = (orgScore / 100) * dimension.weight;
      return {
        dimension: dimId,
        score: normalizedScore,
        weight: dimension.weight,
        category: dimension.category
      };
    });

    const totalScore = dimensionScores.reduce((sum, item) => sum + item.score, 0);
    const maxPossibleScore = dimensionScores.reduce((sum, item) => sum + item.weight, 0);
    
    const maturityPercentage = (totalScore / maxPossibleScore) * 100;

    return {
      overallScore: Math.round(maturityPercentage * 100) / 100,
      categoryScores: this.groupByCategory(dimensionScores),
      dimensionBreakdown: dimensionScores,
      maturityLevel: this.getMaturityLevel(maturityPercentage)
    };
  }

  // Get recommended transformations - FIXED VERSION
  getRecommendedTransformations(organizationData, limit = 10) {
    const maturityScore = this.calculateMaturityScore(organizationData);
    const weaknesses = maturityScore.dimensionBreakdown
      .filter(d => d.score < 0.6 * d.weight)
      .map(d => d.dimension);

    const recommendations = [];

    Object.keys(this.transformations).forEach(transformationId => {
      const transformation = this.transformations[transformationId];
      const impactScore = this.calculateTransformationImpactScore(
        transformation,
        weaknesses,
        organizationData
      );

      const priorityScore = this.calculatePriorityScore(transformation, organizationData);
      
      recommendations.push({
        id: transformationId,
        name: transformation.name,
        description: transformation.description,
        category: transformation.category,
        priority: transformation.priority,
        impactScore: impactScore,
        priorityScore: priorityScore,
        estimatedROI: this.estimateROI(transformation, organizationData),
        timeToValue: transformation.expectedDuration.min,
        implementationComplexity: transformation.complexity,
        cost: transformation.resourceRequirements?.budget?.min || 100000,
        duration: transformation.expectedDuration.min
      });
    });

    // Sort by combined score
    return recommendations
      .sort((a, b) => (b.impactScore * 0.7 + b.priorityScore * 0.3) - (a.impactScore * 0.7 + a.priorityScore * 0.3))
      .slice(0, limit);
  }

  // Calculate transformation impact
  calculateTransformationImpactScore(transformation, weakDimensions, organizationData) {
    let impact = 0;
    
    // Impact on weak dimensions
    const dimensionImpact = weakDimensions.filter(dim => 
      transformation.dimensions?.includes?.(dim) || 
      this.dimensions[dim]?.transformationRelationships?.includes?.(transformation.id)
    ).length;

    impact += dimensionImpact * 0.4;

    // Priority multiplier
    const priorityMultipliers = {
      critical: 1.5,
      high: 1.2,
      medium: 1.0,
      low: 0.8
    };

    impact *= priorityMultipliers[transformation.priority] || 1;

    // Complexity adjustment
    const complexityAdjustments = {
      high: 0.7,
      medium: 0.9,
      low: 1.1
    };

    impact *= complexityAdjustments[transformation.complexity] || 1;

    return Math.min(Math.max(impact, 0), 1);
  }

  // Generate transformation roadmap
  generateRoadmap(organizationData, timeframe = '1y') {
    const recommendations = this.getRecommendedTransformations(organizationData, 20);
    const quarters = timeframe === '1y' ? 4 : timeframe === '2y' ? 8 : 12;
    
    const roadmap = {
      phases: [],
      timeline: [],
      resourceAllocation: {},
      expectedOutcomes: {}
    };

    // Group by priority and complexity
    const criticalHigh = recommendations.filter(t => t.priority === 'critical' && t.implementationComplexity !== 'high');
    const criticalComplex = recommendations.filter(t => t.priority === 'critical' && t.implementationComplexity === 'high');
    const highPriority = recommendations.filter(t => t.priority === 'high');
    const mediumPriority = recommendations.filter(t => t.priority === 'medium');
    
    // Phase 1: Quick Wins (Quarter 1)
    roadmap.phases.push({
      name: "Quick Wins & Foundation",
      duration: "Quarter 1",
      transformations: criticalHigh.slice(0, 3),
      focus: "Immediate impact, low complexity",
      expectedImprovement: "15-25% overall maturity"
    });

    // Phase 2: Strategic Initiatives (Quarters 2-3)
    roadmap.phases.push({
      name: "Strategic Transformation",
      duration: "Quarters 2-3",
      transformations: [
        ...criticalComplex.slice(0, 2),
        ...highPriority.slice(0, 4)
      ],
      focus: "Core capability building",
      expectedImprovement: "35-50% overall maturity"
    });

    // Phase 3: Optimization & Scaling (Quarter 4+)
    roadmap.phases.push({
      name: "Optimization & Scaling",
      duration: `Quarter 4${timeframe !== '1y' ? '+' : ''}`,
      transformations: [
        ...highPriority.slice(4, 6),
        ...mediumPriority.slice(0, 4)
      ],
      focus: "Performance optimization and scaling",
      expectedImprovement: "60-80% overall maturity"
    });

    // Calculate resource requirements
    const totalBudget = roadmap.phases.reduce((sum, phase) => {
      return sum + phase.transformations.reduce((phaseSum, t) => {
        const trans = this.transformations[t.id];
        return phaseSum + (trans.resourceRequirements?.budget?.min || 100000);
      }, 0);
    }, 0);

    roadmap.resourceAllocation = {
      totalBudget: totalBudget,
      quarterlyBreakdown: roadmap.phases.map((phase, index) => ({
        phase: phase.name,
        budget: phase.transformations.reduce((sum, t) => {
          const trans = this.transformations[t.id];
          return sum + (trans.resourceRequirements?.budget?.min || 100000);
        }, 0),
        teamSize: phase.transformations.reduce((sum, t) => {
          const trans = this.transformations[t.id];
          return sum + (trans.resourceRequirements?.teamSize?.core || 3);
        }, 0)
      })),
      roiEstimate: this.calculateRoadmapROI(roadmap, organizationData)
    };

    return roadmap;
  }

  // Q* Optimized sequence planning
  qStarOptimizeSequence(transformations, constraints = {}) {
    const { maxBudget = 1000000, maxTimeline = 365, teamCapacity = 10 } = constraints;
    
    // Use Q* inspired optimization
    const optimizedSequence = [];
    let remainingBudget = maxBudget;
    let remainingTime = maxTimeline;
    let currentCapability = 0;

    // Sort by value-to-effort ratio (Q* approach)
    const scoredTransformations = transformations.map(t => {
      const transformation = this.transformations[t.id];
      const value = t.impactScore * t.priorityScore;
      const effort = transformation.complexity === 'high' ? 3 : 
                     transformation.complexity === 'medium' ? 2 : 1;
      const cost = transformation.resourceRequirements?.budget?.min || 100000;
      const duration = transformation.expectedDuration?.min || 60;

      return {
        ...t,
        valueEffortRatio: value / (effort * cost * duration),
        cost,
        duration
      };
    }).sort((a, b) => b.valueEffortRatio - a.valueEffortRatio);

    // Greedy optimization with constraints
    for (const transformation of scoredTransformations) {
      if (transformation.cost <= remainingBudget && 
          transformation.duration <= remainingTime &&
          transformation.implementationComplexity !== 'high' || currentCapability > 0.5) {
        
        optimizedSequence.push(transformation);
        remainingBudget -= transformation.cost;
        remainingTime -= transformation.duration;
        currentCapability += 0.2; // Learning curve improvement
      }
    }

    return {
      sequence: optimizedSequence,
      totalCost: maxBudget - remainingBudget,
      totalDuration: maxTimeline - remainingTime,
      estimatedValue: optimizedSequence.reduce((sum, t) => sum + t.impactScore, 0),
      efficiency: (maxBudget - remainingBudget) / optimizedSequence.length
    };
  }

  // Helper methods
  groupByCategory(dimensionScores) {
    return dimensionScores.reduce((acc, item) => {
      if (!acc[item.category]) acc[item.category] = { score: 0, count: 0 };
      acc[item.category].score += item.score;
      acc[item.category].count++;
      return acc;
    }, {});
  }

  getMaturityLevel(percentage) {
    if (percentage >= 90) return 'divine';
    if (percentage >= 80) return 'miraculous';
    if (percentage >= 70) return 'unprecedented';
    if (percentage >= 60) return 'exceptional';
    if (percentage >= 50) return 'world_class';
    if (percentage >= 40) return 'competitive';
    if (percentage >= 30) return 'developing';
    return 'foundational';
  }

  estimateROI(transformation, organizationData) {
    const baseROI = 2.5; // 250% average ROI
    const complexityFactor = transformation.complexity === 'high' ? 0.8 : 
                            transformation.complexity === 'medium' ? 1.0 : 1.2;
    const priorityFactor = transformation.priority === 'critical' ? 1.4 :
                           transformation.priority === 'high' ? 1.2 : 1.0;
    
    return (baseROI * complexityFactor * priorityFactor).toFixed(1);
  }

  calculatePriorityScore(transformation, organizationData) {
    let score = 0;
    
    // Time since last similar transformation
    score += 0.3;
    
    // Strategic alignment
    if (transformation.category === organizationData.strategicFocus) {
      score += 0.4;
    }
    
    // Resource availability match
    const resourceMatch = this.calculateResourceMatch(transformation, organizationData);
    score += resourceMatch * 0.3;

    return score;
  }

  calculateResourceMatch(transformation, organizationData) {
    const requiredTeam = transformation.resourceRequirements?.teamSize?.core || 3;
    const availableTeam = organizationData.resources?.teamSize || 5;
    
    const teamMatch = Math.min(requiredTeam / availableTeam, 1);
    
    const requiredBudget = transformation.resourceRequirements?.budget?.min || 100000;
    const availableBudget = organizationData.resources?.budget || 500000;
    
    const budgetMatch = Math.min(requiredBudget / availableBudget, 1);
    
    return (teamMatch + budgetMatch) / 2;
  }

  calculateRoadmapROI(roadmap, organizationData) {
    let totalInvestment = 0;
    let totalValue = 0;

    roadmap.phases.forEach(phase => {
      phase.transformations.forEach(t => {
        const transformation = this.transformations[t.id];
        const investment = transformation.resourceRequirements?.budget?.min || 100000;
        const roi = parseFloat(this.estimateROI(transformation, organizationData));
        
        totalInvestment += investment;
        totalValue += investment * roi;
      });
    });

    return {
      totalInvestment: totalInvestment,
      totalReturn: totalValue,
      netValue: totalValue - totalInvestment,
      roiPercentage: ((totalValue - totalInvestment) / totalInvestment * 100).toFixed(1)
    };
  }

  // ✅ NEW: Initialize organization with default dimension scores
  initializeOrganizationDimensions(organizationData) {
    const dimensions = {};
    Object.keys(this.dimensions).forEach(dimId => {
      dimensions[dimId] = 50; // Default score of 50
    });
    
    return {
      ...organizationData,
      dimensions: dimensions
    };
  }

  // ✅ NEW: Update dimension score
  updateDimensionScore(organizationData, dimensionId, score) {
    return {
      ...organizationData,
      dimensions: {
        ...(organizationData.dimensions || {}),
        [dimensionId]: Math.min(Math.max(score, 0), 100)
      }
    };
  }

simulateImpact(transformations, scenario, organization) {
    // Calculate total investment
    const totalInvestment = transformations.reduce((sum, t) => {
      const trans = this.transformations[t.id] || t;
      return sum + (trans.resourceRequirements?.budget?.min || 100000);
    }, 0);

    // Calculate expected value based on impact scores
    const totalValue = transformations.reduce((sum, t) => {
      const trans = this.transformations[t.id] || t;
      const impactScore = t.impactScore || 0.5;
      const estimatedROI = parseFloat(this.estimateROI(trans, organization)) || 2.5;
      const investment = trans.resourceRequirements?.budget?.min || 100000;
      return sum + (investment * estimatedROI * impactScore);
    }, 0);

    // Calculate success probability based on constraints
    const successProbability = this.calculateSuccessProbability(transformations, scenario);

    // Generate risks based on complexity and constraints
    const risks = this.generateRisks(transformations, scenario);

    return {
      successProbability: Math.min(successProbability, 100),
      roiEstimate: {
        totalInvestment,
        totalReturn: totalValue,
        roiPercentage: ((totalValue - totalInvestment) / totalInvestment * 100).toFixed(1),
        netValue: totalValue - totalInvestment
      },
      scenarios: 10000, // Monte Carlo simulations
      confidenceInterval: 95,
      risks,
      recommendations: this.generateRecommendations(transformations, scenario, organization)
    };
  }

  // ✅ ADD THESE HELPER FUNCTIONS
  calculateSuccessProbability(transformations, constraints) {
    let baseProbability = 80; // Base success rate
    
    // Adjust based on risk tolerance
    baseProbability *= constraints.riskTolerance || 0.5;
    
    // Adjust based on team capacity
    const requiredTeam = transformations.reduce((sum, t) => {
      const trans = this.transformations[t.id] || t;
      return sum + (trans.resourceRequirements?.teamSize?.core || 3);
    }, 0);
    
    if (requiredTeam > (constraints.teamCapacity || 5)) {
      baseProbability *= 0.8; // Reduce probability if team is overloaded
    }
    
    // Adjust based on timeline
    const requiredTime = transformations.reduce((sum, t) => {
      const trans = this.transformations[t.id] || t;
      return sum + (trans.expectedDuration?.min || 60);
    }, 0);
    
    if (requiredTime > (constraints.maxTimeline || 180)) {
      baseProbability *= 0.7; // Reduce probability if timeline is tight
    }
    
    return Math.min(baseProbability, 100);
  }

  generateRisks(transformations, constraints) {
    const risks = [];
    
    // Budget risk
    const totalBudget = transformations.reduce((sum, t) => {
      const trans = this.transformations[t.id] || t;
      return sum + (trans.resourceRequirements?.budget?.min || 100000);
    }, 0);
    
    if (totalBudget > constraints.maxBudget) {
      risks.push({
        type: 'Budget Overrun',
        probability: 65,
        impact: 'High',
        description: 'Project may exceed allocated budget'
      });
    }
    
    // Complexity risk
    const complexTransformations = transformations.filter(t => {
      const trans = this.transformations[t.id] || t;
      return trans.complexity === 'high';
    }).length;
    
    if (complexTransformations > 2) {
      risks.push({
        type: 'High Complexity',
        probability: 50,
        impact: 'Medium',
        description: 'Multiple high-complexity transformations may slow progress'
      });
    }
    
    // Team capacity risk
    const requiredTeam = transformations.reduce((sum, t) => {
      const trans = this.transformations[t.id] || t;
      return sum + (trans.resourceRequirements?.teamSize?.core || 3);
    }, 0);
    
    if (requiredTeam > (constraints.teamCapacity || 5)) {
      risks.push({
        type: 'Team Overload',
        probability: 40,
        impact: 'Medium',
        description: 'Team may be stretched too thin across multiple initiatives'
      });
    }
    
    // Timeline risk
    const requiredTime = transformations.reduce((sum, t) => {
      const trans = this.transformations[t.id] || t;
      return sum + (trans.expectedDuration?.min || 60);
    }, 0);
    
    if (requiredTime > (constraints.maxTimeline || 180)) {
      risks.push({
        type: 'Timeline Pressure',
        probability: 55,
        impact: 'High',
        description: 'Aggressive timeline may lead to quality compromises'
      });
    }
    
    return risks;
  }

  generateRecommendations(transformations, scenario, organization) {
    const recommendations = [];
    
    if (transformations.length > 5) {
      recommendations.push('Consider reducing the number of concurrent transformations to maintain focus');
    }
    
    const highComplexity = transformations.filter(t => {
      const trans = this.transformations[t.id] || t;
      return trans.complexity === 'high';
    }).length;
    
    if (highComplexity > 1) {
      recommendations.push('Stagger high-complexity transformations to manage risk');
    }
    
    // Budget optimization
    const totalBudget = transformations.reduce((sum, t) => {
      const trans = this.transformations[t.id] || t;
      return sum + (trans.resourceRequirements?.budget?.min || 100000);
    }, 0);
    
    if (totalBudget > scenario.maxBudget * 0.8) {
      recommendations.push('Consider prioritizing transformations with higher ROI to optimize budget');
    }
    
    return recommendations.join(' ') || 'Proceed with current plan. All constraints are well within limits.';
  }

  // ✅ ADD OTHER MISSING FUNCTIONS IF NEEDED
  calculateDimensionImpact(dimensionId, transformationId, organization) {
    // Simplified impact calculation
    const transformation = this.transformations[transformationId];
    const dimension = this.dimensions[dimensionId];
    
    if (!transformation || !dimension) return 0;
    
    // Check if transformation relates to this dimension
    const hasRelationship = dimension.transformationRelationships?.includes(transformationId) ||
                           transformation.dimensions?.includes(dimensionId);
    
    if (!hasRelationship) return 0;
    
    // Calculate impact based on transformation priority and dimension weight
    const priorityMultipliers = {
      critical: 1.0,
      high: 0.8,
      medium: 0.6,
      low: 0.4
    };
    
    const baseImpact = (transformation.impactScore || 0.5) * 
                      (dimension.weight || 0.05) * 
                      (priorityMultipliers[transformation.priority] || 0.6);
    
    return Math.min(baseImpact * 100, 100);
  }

  generateCustomRoadmap(selectedTransformations, timeframe, organization) {
    // Generate a custom roadmap based on selected transformations
    const transformations = selectedTransformations.map(id => this.transformations[id]).filter(Boolean);
    
    const phases = this.createPhasesFromTransformations(transformations, timeframe);
    
    return {
      phases,
      resourceAllocation: this.calculateResourceAllocation(transformations),
      expectedOutcomes: this.calculateExpectedOutcomes(transformations, organization)
    };
  }

  createPhasesFromTransformations(transformations, timeframe) {
    // Simplified phase creation logic
    const quarterDays = timeframe === '1y' ? 90 : timeframe === '2y' ? 180 : 365;
    
    return [
      {
        name: "Phase 1: Quick Wins",
        duration: `${quarterDays} days`,
        transformations: transformations.slice(0, Math.min(3, transformations.length)),
        focus: "Immediate impact and momentum building"
      },
      {
        name: "Phase 2: Core Implementation",
        duration: `${quarterDays * 2} days`,
        transformations: transformations.slice(3, Math.min(7, transformations.length)),
        focus: "Core capability development"
      },
      {
        name: "Phase 3: Optimization",
        duration: `${quarterDays} days`,
        transformations: transformations.slice(7),
        focus: "Performance optimization and scaling"
      }
    ];
  }

  calculateResourceAllocation(transformations) {
    const totalBudget = transformations.reduce((sum, t) => 
      sum + (t.resourceRequirements?.budget?.min || 100000), 0);
    
    const totalTeam = transformations.reduce((sum, t) => 
      sum + (t.resourceRequirements?.teamSize?.core || 3), 0);
    
    const totalDuration = transformations.reduce((sum, t) => 
      sum + (t.expectedDuration?.min || 60), 0);
    
    return {
      totalBudget,
      totalTeam,
      totalDuration,
      quarterlyBreakdown: this.createQuarterlyBreakdown(transformations)
    };
  }

  createQuarterlyBreakdown(transformations) {
    // Simplified quarterly breakdown
    return [
      {
        phase: "Quarter 1",
        budget: transformations.slice(0, 3).reduce((sum, t) => sum + (t.resourceRequirements?.budget?.min || 100000), 0),
        teamSize: transformations.slice(0, 3).reduce((sum, t) => sum + (t.resourceRequirements?.teamSize?.core || 3), 0)
      },
      {
        phase: "Quarter 2",
        budget: transformations.slice(3, 6).reduce((sum, t) => sum + (t.resourceRequirements?.budget?.min || 100000), 0),
        teamSize: transformations.slice(3, 6).reduce((sum, t) => sum + (t.resourceRequirements?.teamSize?.core || 3), 0)
      },
      {
        phase: "Quarter 3",
        budget: transformations.slice(6, 9).reduce((sum, t) => sum + (t.resourceRequirements?.budget?.min || 100000), 0),
        teamSize: transformations.slice(6, 9).reduce((sum, t) => sum + (t.resourceRequirements?.teamSize?.core || 3), 0)
      }
    ];
  }

  calculateExpectedOutcomes(transformations, organization) {
    const totalROI = transformations.reduce((sum, t) => 
      sum + parseFloat(this.estimateROI(t, organization) || 2.5), 0);
    
    const avgROI = totalROI / transformations.length;
    
    return {
      expectedROI: avgROI.toFixed(1),
      timeToValue: `${transformations.reduce((sum, t) => sum + (t.expectedDuration?.min || 60), 0)} days`,
      keyBenefits: [
        "Improved operational efficiency",
        "Enhanced strategic capabilities",
        "Increased competitive advantage",
        "Better resource utilization"
      ]
    };
  }

}
//module.exports = IEPEngine;
export default IEPEngine;
